import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory27() {
  const { lesson, course, accent } = findLesson("music-theory", "27");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT27-Chord-Trees-Diatonic-Animated.mp4"
        caption="Diatonic chord trees — each scale note lives in exactly 3 chords"
        hint="≈ 32 sec · watch first"
      />
      <Chapter number="27" eyebrow="Music Theory · Chapter Twenty-Seven" title="Chord trees · Part 2: a tree for every note in the key">
        <p>
          The chromatic chord tree from the last chapter casts the widest possible net —
          six branches that reach outside any single key, on every note in music. That
          freedom is wonderful for film scoring, ballads, jazz reharmonisation. It can
          also be too much when you're trying to figure out a pop song that obeys the
          rules of one key.
        </p>
        <p>
          The diatonic chord tree is the same idea, narrowed. Stay inside the seven notes
          of one scale. Stay inside the seven chords those notes produce. The tree gets
          shorter, the choices fewer — and the answer to "what chord goes under this
          melody note?" gets sharper.
        </p>
        <MTQuote>
          What I like to imagine is each note of music is a tree and their branches would be
          the chords. Now on the chords, if you imagine a dynamically moving creature like a
          monkey, it moves on the branch. So it's moving on the chord, and hence while it
          moves on the chord, that movement is the melody.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The metaphor" title="Monkey on a tree">
        <Prose>
          <p>
            Picture a tree. The trunk is a note from the scale. The branches growing out
            of it are the chords that contain that note — and on each branch sits a
            monkey, the genius of trees, leaping between branches as it likes. The
            <strong> monkey's path is the melody</strong>; the <strong>branch it lands
            on is the harmony</strong>. Sometimes it stays on one tree and explores all
            the branches; sometimes it jumps to a neighbouring tree because the next
            landing note in the melody is a different scale degree.
          </p>
          <p>
            This is not just a cute metaphor. It's a working visualisation for everything
            that happens when you compose a melody to fit chords, or chords to fit a
            melody, or reharmonise an existing piece. Every important note of the melody
            is a tree. Every chord choice is a branch. The whole song is a journey
            through a small forest of seven trees.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The seven trees of F major" title="Diatonic, by definition">
        <Prose>
          <p>
            We did C and A as chromatic trees in the last chapter. Let's do
            <strong> F major</strong> diatonically here — same idea, restricted to the
            scale. First, write down the diatonic chords of F major. The key has one
            flat (B♭), so the seven triads are:
          </p>
        </Prose>

        <TheoryBox label="The diatonic chords of F major">
          <div className="font-mono text-[14px] text-amber-200">
            I = F maj &nbsp;·&nbsp; ii = G min &nbsp;·&nbsp; iii = A min &nbsp;·&nbsp; IV = B♭ maj &nbsp;·&nbsp; V = C maj &nbsp;·&nbsp; vi = D min &nbsp;·&nbsp; vii° = E dim
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Three major chords, three minor chords, one diminished — the same pattern
            in any major key.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            And the seven scale notes are <span className="font-mono text-amber-300">F, G, A, B♭, C, D, E</span>.
            One tree per note. Each tree contains only the diatonic chords that have that
            note in them — no chord from outside the key.
          </p>
        </Prose>

        <ChordTree tonic="F" mode="major" />
      </Chapter>

      <Chapter eyebrow="The tree of F" title="Three branches per scale tone">
        <Prose>
          <p>
            Take the note <strong>F</strong>. Which diatonic chords of F major contain it?
            Run the same three questions from the last chapter:
          </p>
        </Prose>

        <Steps>
          <Step><strong>F as the root.</strong> <span className="font-mono text-amber-300">F major</span> (F&nbsp;A&nbsp;C). The tonic.</Step>
          <Step><strong>F as the perfect fifth.</strong> The fifth above B♭ is F — so <span className="font-mono text-amber-300">B♭ major</span> (B♭&nbsp;D&nbsp;F). That's the IV chord.</Step>
          <Step><strong>F as the minor third.</strong> A minor third above D is F — so <span className="font-mono text-amber-300">D minor</span> (D&nbsp;F&nbsp;A). That's the vi chord.</Step>
        </Steps>

        <Prose>
          <p>
            Three branches: <strong>I, IV, vi</strong>. Always three for a diatonic tree
            (because every scale note appears in exactly three triads of the key). The
            chromatic tree of F had six branches reaching outside the key — D♭ major,
            B♭ minor, F minor — but for diatonic work in F major, those are
            <em> borrowed chords</em>. We leave them off this map.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The tree of G" title="A second worked example">
        <Prose>
          <p>
            Now do the same for <strong>G</strong>. Which diatonic chords of F major
            contain G?
          </p>
        </Prose>

        <Steps>
          <Step><strong>G as the root.</strong> <span className="font-mono text-amber-300">G minor</span> (G&nbsp;B♭&nbsp;D). That's the ii.</Step>
          <Step><strong>G as the perfect fifth.</strong> The fifth above C is G — <span className="font-mono text-amber-300">C major</span> (C&nbsp;E&nbsp;G). The V chord.</Step>
          <Step><strong>G as the minor third.</strong> A minor third above E is G — but in F major the chord on E is diminished: <span className="font-mono text-amber-300">E dim</span> (E&nbsp;G&nbsp;B♭). The vii°.</Step>
        </Steps>

        <Prose>
          <p>
            Three more branches: <strong>ii, V, vii°</strong>. So when you land on G in a
            song in F major, you have G minor, C major, or E diminished as your harmonic
            choices. The most common pop instinct is to grab G minor (the namesake-root
            chord), but the tree opens up two more — and choosing the V (C major) under a
            G in the melody is what makes you sound like a composer instead of a
            beginner.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="From tree to song" title="Walking the forest">
        <Prose>
          <p>
            Once you have all seven trees mapped, harmonising a melody becomes mechanical.
            For each landing note in the tune, you have three diatonic candidates. You
            pick the one whose colour suits the lyric, the contour, the previous chord and
            the next chord. That last bit — context with neighbouring chords — is what
            keeps you from picking the same I or vi over and over.
          </p>
          <p>
            This is also a useful framing for what some books call <em>reharmonisation</em> —
            taking a song you already know and swapping in a different chord under the
            same melody. The tree shows you, in advance, exactly which swaps preserve the
            key. Anything else is a borrowed chord, which is the next step beyond.
          </p>
        </Prose>

        <TheoryBox label="The diatonic-tree shortcut">
          For any note <em>n</em> in a major key, its three diatonic chords are
          <strong> the chord on n</strong>, <strong>the chord a third below n</strong>,
          and <strong>the chord a fifth below n</strong>. Three chords. Always. That's
          the entire trick.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Why writing matters" title="Pen, paper, every key">
        <Prose>
          <p>
            Reading about chord trees is not the same as knowing them. The fastest path
            to fluency is the one we'd use for algebra or geometry in school: write
            everything down. Pick a key — F major, then C, then G, then D — and write the
            seven trees of that key, three branches each. Then go to the next key. By
            the time you've done all twelve, you will not be looking up chord choices
            any more. You will be generating them on demand at the keyboard.
          </p>
          <p>
            And do not write in a line. Write the scale in a circle (clockwise for
            ascending, counter-clockwise for descending) so the seven notes are visible
            to your eye in their cyclic relationship. Reading scales as a row in a PDF
            is exactly why most students never internalise them.
          </p>
        </Prose>

        <Glossary>
          <Term name="Diatonic chord tree" def="For each note of a major key, the three diatonic chords that contain it. Always I-style, IV-style, and vi-style relationships." />
          <Term name="Branch" def="A chord on a tree. In the diatonic tree, each tree has exactly three branches." />
          <Term name="Borrowed chord" def="A chord that contains the landing note but is not in the diatonic tree — taken from a related scale (parallel minor, modal mixture, etc.)." />
          <Term name="Reharmonisation" def="Replacing the chords under an existing melody. The chord tree is the working tool for doing it inside one key." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Try the tree of A minor" title="The minor side of the same forest">
        <Prose>
          <p>
            Major isn't the only place trees grow. The relative minor of C major is
            <strong> A minor</strong> — same seven notes, different tonic. The seven
            trees are the same trees in a different order: A minor's i, iv, v are the
            vi, ii, iii of C major. Toggle the player below to <em>minor</em> and try it.
          </p>
        </Prose>

        <ChordTree tonic="A" mode="minor" />
        <NoteImage
          src="/notes/music-theory/finals/27a-diatonic-chord-trees-F-major-system.png"
          caption="Diatonic chord trees of F major — every chord in the key (F, Gm, Am, B♭, C, Dm, E°) gets its OWN tree of triads it relates to. Hover over any node and you see how that chord 'lives' in the key relative to the others. Composers use these trees to find substitutions that still sound diatonic."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-28"
        label="Chapter 28 · Chord Trees - Hey Jude (Part 3)"
        why="The Beatles wrote 'Hey Jude' in F major — exactly the key we just mapped. We'll use the chord tree to predict the chords from the melody alone, then compare to what they actually played."
      />
    </LessonShell>
  );
}
